import { useCallback, useEffect, useRef, useState } from "react";
import { useSectionTracking } from "@/lib/useSectionTracking";
import { PlatformSuite, CommitmentPath } from "@/pages/rtd/PlatformPitch";
import { DueDiligenceGuide } from "@/pages/rtd/DueDiligence";
import { ExpertQuotes, ClientQuotes } from "@/pages/rtd/ExpertQuotes";
import { RtdDataPrivacy } from "@/pages/rtd/DataPrivacy";
import { EngagementBuilder } from "@/pages/rtd/EngagementBuilder";
import { PlatformScreens } from "@/components/PlatformScreens";
import { toast } from "sonner";
import {
  ArrowRight, CheckCircle2, Mail, Loader2, Send, ChevronRight, Printer,
  Sparkles, FileSignature, RefreshCw, ListChecks, ExternalLink, Phone,
  Building2, Radio, ShieldAlert, Gift, Play, MapPin, Star, Clock, Bell,
  BarChart3, Library, MessageSquare, Users, BookOpen, ShieldCheck,
  Zap, Award, AlertTriangle, Calendar,
} from "lucide-react";
import { submitRtdOrder, pingRtdStar } from "@/lib/api";

/* ============================================================================
 * RTD Financial — Tailored Microsite (SEC-registered RIA · cloned from the Sage/Giggle pitch)
 * Audience: RTD compliance leadership + approvers. Primary CTA: the Plan Builder (#build).
 * Brand: Mulish · royal blue #0E509C / navy #10346E · orange accent #E0663F
 * ========================================================================== */

const CYAN_LIGHT = "#4F86D6";
const CYAN_DARK = "#0E509C";
const PINK_DARK = "#10346E";
const PINK_LIGHT = "#E0663F";
const INK = "#0F1B2D";

const TRAINING_REQUEST_URL = "https://www.complisun.com/request-training-pc/";
const LAYERPATH_TOUR_URL = "https://share.layerpath.com/e/cmbo6lode0006l4077dav8qom/tour";
const REQUEST_TRAINING_VIDEO = "https://customer-assets.emergentagent.com/job_complisun-pitch/artifacts/5t0ww80u_RequestingTraining.mp4";

// Hoisted so the IntersectionObserver effect can safely use [] deps.
const TRACKED_SECTIONS = [
  "suite", "diligence", "path", "build", "why", "expert", "answers", "courses", "calendar",
  "inside", "privacy", "tracking", "addons", "pricing", "timeline", "order",
];

export default function RtdProposal() {
  const [seatCount, setSeatCount] = useState(40);
  const [starredAddons, setStarredAddons] = useState(() => new Set());
  const [viewerEmail, setViewerEmail] = useState(null);
  const { seenSections, engagementPct } = useSectionTracking(TRACKED_SECTIONS);

  useEffect(() => {
    const id = "rtd-fonts";
    if (!document.getElementById(id)) {
      const link = document.createElement("link");
      link.id = id;
      link.rel = "stylesheet";
      link.href = "https://fonts.googleapis.com/css2?family=Mulish:wght@400;500;600;700;800;900&display=swap";
      document.head.appendChild(link);
    }
    const prev = document.title;
    document.title = "CompliSun · A compliance partner for RTD Financial";
    return () => { document.title = prev; };
  }, []);

  const handlePrint = () => window.print();
  const jumpToOrder = () => {
    const el = document.getElementById("order");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div
      data-testid="rtd-proposal"
      style={{ background: "#fff", color: INK, fontFamily: "'Mulish', system-ui, -apple-system, sans-serif" }}
      className="min-h-screen w-full antialiased"
    >
      <style>{`
        .rt-h { font-family: 'Mulish', sans-serif; font-weight: 900; letter-spacing: -0.02em; line-height: 1.05; }
        .rt-eyebrow { font-family: 'Mulish', sans-serif; font-weight: 800; letter-spacing: 0.18em; text-transform: uppercase; font-size: 11px; color: ${CYAN_DARK}; }
        .rt-grad-text { background: linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 60%, ${PINK_DARK} 100%); -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text; }
        .rt-grad-bg-cyan { background: linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 100%); }
        .rt-grad-bg-pink { background: linear-gradient(135deg, #3A3A3A 0%, ${PINK_DARK} 100%); }
        .rt-cta { background: linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 100%); color: #fff; transition: transform .2s ease, box-shadow .2s ease, filter .2s ease; border-radius: 999px; font-weight: 800; }
        .rt-cta:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -10px ${CYAN_DARK}80; filter: brightness(1.05); }
        .rt-cta-pink { background: ${PINK_DARK}; color: #fff; transition: transform .2s ease, box-shadow .2s ease; border-radius: 999px; font-weight: 800; }
        .rt-cta-pink:hover { transform: translateY(-2px); box-shadow: 0 12px 28px -10px ${PINK_DARK}80; }
        .rt-card { background: #fff; border: 1.5px solid #E5EDF5; border-radius: 24px; transition: border-color .2s ease, transform .2s ease, box-shadow .2s ease; }
        .rt-card:hover { border-color: ${CYAN_LIGHT}55; box-shadow: 0 12px 32px -16px rgba(14,80,156,0.18); }
        .rt-chip { background: ${CYAN_LIGHT}1A; color: ${CYAN_DARK}; border: 1px solid ${CYAN_LIGHT}55; border-radius: 999px; padding: 6px 14px; font-size: 12px; font-weight: 700; }
        .rt-chip-pink { background: ${PINK_LIGHT}26; color: ${PINK_DARK}; border: 1px solid ${PINK_LIGHT}88; border-radius: 999px; padding: 6px 14px; font-size: 12px; font-weight: 700; }
        .anchor-target { scroll-margin-top: 96px; }
        ::-webkit-scrollbar { width: 12px; height: 12px; }
        ::-webkit-scrollbar-track { background: rgba(17,17,17,0.06) !important; }
        ::-webkit-scrollbar-thumb { background: rgba(17,17,17,0.35) !important; border-radius: 6px; border: 2px solid #fff; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(17,17,17,0.55) !important; }
        html { scrollbar-width: thin; scrollbar-color: rgba(17,17,17,0.45) rgba(17,17,17,0.06); }
        @media print {
          @page { size: Letter; margin: 0.5in; }
          [data-print="hide"], [data-testid="rtd-nav"] { display: none !important; }
          .rt-card { box-shadow: none !important; border-color: #D6DBE6 !important; break-inside: avoid; }
          section { break-inside: avoid-page; padding-top: 24px !important; padding-bottom: 24px !important; }
          .rt-cta, .rt-cta-pink { background: #fff !important; color: ${INK} !important; border: 2px solid ${CYAN_DARK} !important; }
          iframe { display: none !important; }
          video { display: none !important; }
          [data-print-note] { display: block !important; }
        }
      `}</style>

      <NavBar onPrint={handlePrint} />

      <main className="max-w-6xl mx-auto px-6 md:px-10">
        <Hero onJumpToOrder={jumpToOrder} onPrint={handlePrint} />
        <PlatformSuite />
        <ClientQuotes />
        <DueDiligenceGuide />
        <PlatformScreens
          id="inside"
          eyebrowClass="rt-eyebrow"
          headingClass="rt-h"
          cardClass="rt-card"
          accent="#0E509C"
          accentSpan="rt-grad-text"
          wrapClass=""
          lightbox
          eyebrow="Inside the platform · intuitive modules"
          headingText="Intuitive modules that tell your compliance story."
          headingAccentText="Designed around how you work."
          intro="Clear flows your dedicated expert and your team share — so compliance gets taken care of proactively, and the record proves it. When an examiner asks how compliance works at RTD Financial, the platform is the answer."
          footnote="Screens shown with sample data — the prototype is configured to RTD Financial's workflows, and your team clicks through it before anyone signs."
        />
        <RtdDataPrivacy />
        <CommitmentPath />
        <EngagementBuilder
          engagementPct={engagementPct}
          sectionsViewed={Array.from(seenSections)}
          onPrint={handlePrint}
        />
        <WhyUs />
        <ExpertQuotes />
        <ThreeAnswers />
        <CoursesOverview />
        <BuildYourOwn />
        <StateLibrary />
        <TrackingSample />
        <AddOnBundle picked={starredAddons} setPicked={setStarredAddons} viewerEmail={viewerEmail} />
        <Pricing seats={seatCount} setSeats={setSeatCount} />
        <Timeline />
        <OrderForm
          starredAddons={starredAddons}
          seats={seatCount}
          engagementPct={engagementPct}
          sectionsViewed={Array.from(seenSections)}
          onEmailChange={setViewerEmail}
        />
        <Close />
      </main>

      <Footer />
    </div>
  );
}

/* --------------------- NAV ------------------------------------------------ */
function NavBar({ onPrint }) {
  const items = [
    ["The suite",   "suite"],
    ["How it works", "path"],
    ["Build yours", "build"],
    ["Data privacy", "privacy"],
    ["Calendar",    "calendar"],
    ["Courses",     "courses"],
    ["Pricing",     "build"],
  ];
  return (
    <header
      data-testid="rtd-nav"
      data-print="hide"
      className="sticky top-0 z-30 backdrop-blur-md"
      style={{ background: "rgba(255,255,255,0.95)", borderBottom: `1px solid ${CYAN_LIGHT}22` }}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0 flex-shrink-0">
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-7 w-7 object-contain" />
          <div className="rt-h text-[15px] hidden sm:block">CompliSun</div>
          <span className="text-[10px] font-extrabold uppercase tracking-[0.16em] hidden lg:inline-block ml-2 px-2 py-1 rounded-full"
                style={{ background: `${PINK_DARK}12`, color: PINK_DARK }}>
            For RTD Financial
          </span>
        </div>
        <nav className="hidden md:flex items-center gap-0.5">
          {items.map(([label, id]) => (
            <a key={label} href={`#${id}`} data-testid={`nav-${id}`}
               className="text-[13px] font-bold px-2.5 py-2 rounded-full whitespace-nowrap transition-colors hover:bg-[#E5EDF5]"
               style={{ color: INK }}>
              {label}
            </a>
          ))}
        </nav>
        <div className="flex items-center gap-2 flex-shrink-0">
          <button type="button" onClick={onPrint} data-testid="nav-print"
                  title="Download as PDF"
                  className="hidden md:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-bold uppercase tracking-[0.10em] border"
                  style={{ borderColor: `${CYAN_DARK}30`, color: CYAN_DARK }}>
            <Printer className="h-3.5 w-3.5" /> PDF
          </button>
          <a href="#build" data-testid="nav-order"
             className="rt-cta-pink inline-flex items-center gap-2 h-10 px-4 text-[13px] whitespace-nowrap">
            Plan Builder <ArrowRight className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </header>
  );
}

/* --------------------- HERO ----------------------------------------------- */
function Hero({ onJumpToOrder, onPrint }) {
  return (
    <section className="pt-14 md:pt-20 pb-12 md:pb-16 relative">
      <div className="absolute -top-20 right-0 w-72 h-72 rounded-full opacity-20 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${PINK_LIGHT} 0%, transparent 70%)` }} />
      <div className="absolute -bottom-20 left-0 w-96 h-96 rounded-full opacity-15 pointer-events-none"
           style={{ background: `radial-gradient(circle, ${CYAN_LIGHT} 0%, transparent 70%)` }} />

      <div className="relative">
        <div className="flex flex-col items-start gap-4 mb-6">
          <img src="/brand/prospects/rtd-financial.png" alt="RTD Financial"
               className="h-14 md:h-16 object-contain" data-testid="rtd-logo" />
          <span className="rt-eyebrow">A tailored proposal · prepared by CompliSun for RTD Financial</span>
        </div>

        <h1 className="rt-h text-5xl sm:text-6xl lg:text-7xl max-w-5xl" data-testid="hero-headline">
          Compliance at your fingertips. <span className="rt-grad-text">Built around how you work — so you can focus on your clients.</span>
        </h1>

        <p className="mt-6 text-xl leading-relaxed max-w-3xl" style={{ color: INK, opacity: 0.78 }}>
          Intuitive compliance tools, designed with you. Compliance experts on the ready. And a price
          that's a no-brainer: <strong>$4,000 to install, $1,000/mo for everything.</strong> Demo next
          week, prototype right after, <strong>live in 45 days</strong>.
        </p>

        <div className="mt-6 rt-card p-5 md:p-6 max-w-3xl" data-testid="hero-transition-offer"
             style={{ borderColor: `${PINK_LIGHT}80`, background: `${PINK_LIGHT}0f` }}>
          <div className="rt-eyebrow mb-2" style={{ color: PINK_DARK }}>End-of-quarter offer · sign this month</div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-[14px] leading-snug" style={{ color: INK }}>
            <div><strong>Pay the install now — or in installments.</strong><div className="opacity-70 mt-0.5">Start using the platform immediately and begin the transition.</div></div>
            <div><strong>$1,000/mo starts only when you transition off your current platform.</strong><div className="opacity-70 mt-0.5">We time the monthly fee to your cut-over so you're never paying twice.</div></div>
            <div><strong>Experts run a gap analysis the whole way.</strong><div className="opacity-70 mt-0.5">Advice and hands-on help throughout the transition at the discounted $150/hr rate.</div></div>
          </div>
        </div>

        <div className="mt-9 flex flex-wrap items-center gap-3" data-print="hide">
          <a href="#build" data-testid="hero-order-cta"
             className="rt-cta-pink inline-flex items-center gap-2 h-14 px-7 text-base">
            Open the Plan Builder <ArrowRight className="h-4 w-4" />
          </a>
          <a href="#suite" className="rt-cta inline-flex items-center gap-2 h-14 px-7 text-base">
            See the full suite <ChevronRight className="h-4 w-4" />
          </a>
          <button type="button" onClick={onPrint} data-testid="hero-download-pdf"
                  className="inline-flex items-center gap-2 h-14 px-6 rounded-full text-base font-bold border-2"
                  style={{ borderColor: `${INK}18`, color: INK }}>
            <Printer className="h-4 w-4" /> Download PDF
          </button>
        </div>

        <div className="mt-10 flex flex-wrap items-center gap-2">
          <span className="rt-chip">$1,000/mo · everything included</span>
          <span className="rt-chip">$4,000 install · installments available</span>
          <span className="rt-chip-pink">Live in 45 days</span>
          <span className="rt-chip">Demo next week · prototype right after</span>
          <span className="rt-chip">SOC 2 Type II compliant</span>
          <span className="rt-chip">Monthly fee starts only after you transition</span>
        </div>
      </div>
    </section>
  );
}

/* --------------------- WHY COMPLISUN -------------------------------------- */
function WhyUs() {
  const pillars = [
    {
      icon: Users,
      title: "A proactive partner — not a passive vendor",
      blurb: "You get a named compliance expert who knows RTD, checks in on your cadence, and shows up with ideas: 'the SEC's exam priorities just dropped — here's what changes for you.' You are never a ticket number.",
    },
    {
      icon: Zap,
      title: "A platform that keeps getting better",
      blurb: "Rapid prototyping is how we work — prototype in ~2 weeks, live in 45 days. When a client asks for a feature, we build it. Features useful to everyone get built centrally, so RTD is rarely billed for improvements. Bespoke builds stay inexpensive.",
    },
    {
      icon: ShieldCheck,
      title: "Compliance-expert-first design",
      blurb: "Every screen is designed by practitioners who run live programs and sit through exams — then engineered by a fintech-grade team. That's why the platform knows what a quarterly transaction report actually needs to prove.",
    },
    {
      icon: Award,
      title: "Expert-in-the-loop AI, inside your environment",
      blurb: "AI drafts, our experts validate, and your data never leaves your environment — no raw client data sent to an external model. SOC 2 Type II compliant hosting on AWS with client-specific storage. Trust, then verify — with the logs to prove it.",
    },
  ];
  return (
    <section id="why" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-12">
        <div className="rt-eyebrow mb-3">Why CompliSun</div>
        <h2 className="rt-h text-4xl md:text-6xl">
          We know how to <span className="rt-grad-text">listen, too.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="why-grid">
        {pillars.map((p) => {
          const Ic = p.icon;
          return (
            <article key={p.title} className="rt-card p-7 flex gap-5" data-testid={`why-${p.title.toLowerCase().replace(/[^a-z]+/g, "-").slice(0, 28)}`}>
              <div className="rt-grad-bg-cyan flex-shrink-0"
                   style={{ width: 56, height: 56, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff" }}>
                <Ic className="h-6 w-6" strokeWidth={2.2} />
              </div>
              <div>
                <h3 className="rt-h text-xl">{p.title}</h3>
                <p className="mt-2 text-[15px] leading-relaxed" style={{ color: INK, opacity: 0.78 }}>{p.blurb}</p>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------- THREE ANSWERS -------------------------------------- */
function ThreeAnswers() {
  const items = [
    {
      n: "Q1",
      q: "Existing content or built to order?",
      a: (
        <>
          <strong>Both &mdash; that&rsquo;s the point.</strong> You get immediate access to our maintained
          library (updated <strong>monthly</strong>). On top of that we create new content tailored
          to RTD Financial: keyed off your own compliance manual and Code of Ethics, so advisors attest to
          <em> your</em> program, not boilerplate. A bespoke module ships in about a week.
        </>
      ),
      icon: Library,
    },
    {
      n: "Q2",
      q: "How do you keep the content current?",
      a: (
        <>
          Keeping it current is the day job. Our practitioners run live programs under the
          <strong> Advisers Act</strong>, so SEC exam priorities, Marketing Rule FAQs, risk alerts, and
          enforcement trends roll into the library as they land — and your dedicated expert flags
          what changes for RTD Financial <strong>before</strong> the next exam cycle, not after.
        </>
      ),
      icon: RefreshCw,
    },
    {
      n: "Q3",
      q: "Can you host our or third-party content?",
      a: (
        <>
          <strong>Yes.</strong> The platform hosts content developed by RTD Financial or a third party alongside
          CompliSun-authored modules, with one unified system for assignment, tracking, assessment,
          and attestation across all of it — including the annual Code of Ethics acknowledgment.
        </>
      ),
      icon: Users,
    },
  ];
  return (
    <section id="answers" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-12">
        <div className="rt-eyebrow mb-3">The three answers</div>
        <h2 className="rt-h text-4xl md:text-6xl">
          Training, <span className="rt-grad-text">answered straight.</span>
        </h2>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="three-answers-grid">
        {items.map((it) => {
          const Ic = it.icon;
          return (
            <article key={it.n} className="rt-card p-7 flex flex-col" data-testid={`answer-${it.n.toLowerCase()}`}>
              <div className="flex items-center justify-between mb-4">
                <span className="rt-chip-pink">{it.n}</span>
                <Ic className="h-5 w-5" style={{ color: CYAN_DARK }} strokeWidth={1.8} />
              </div>
              <h3 className="rt-h text-xl leading-snug">{it.q}</h3>
              <p className="mt-3 text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.82 }}>{it.a}</p>
            </article>
          );
        })}
      </div>
    </section>
  );
}

/* --------------------- COURSES OVERVIEW ----------------------------------- */
function CoursesOverview() {
  const types = [
    "Code of Ethics & personal trading",
    "Fiduciary duty & Advisers Act fundamentals",
    "SEC Marketing Rule (206(4)-1)",
    "Form ADV, Form CRS & disclosure obligations",
    "Books & records (Rule 204-2)",
    "Custody rule & safeguarding client assets",
    "ERISA fiduciary & 401(k) plan governance",
    "Reg S-P privacy, cybersecurity & incident response",
    "Senior & vulnerable investor protection",
    "Insider trading & material non-public information",
    "Best execution, trade allocation & soft dollars",
    "Anti-money laundering for advisers (2028 rule)",
    "Vendor / third-party oversight",
    "Workplace conduct, ethics & whistleblower",
    "Information security & phishing awareness",
    "Enterprise risk management",
    "SMART Goals & goal-setting using latest research",
    "Negotiation & business writing for compliance teams",
  ];
  return (
    <section id="courses" className="anchor-target py-14 md:py-20">
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5">
          <div className="rt-eyebrow mb-3">The catalog</div>
          <h2 className="rt-h text-4xl md:text-5xl">
            From the <span className="rt-grad-text">Code of Ethics</span> to
            <span className="rt-grad-text"> SMART Goals.</span>
          </h2>
          <p className="mt-5 text-lg leading-relaxed" style={{ color: INK, opacity: 0.78 }}>
            The current catalog spans compliance to leadership skills, and is updated monthly based
            on your needs. We have <strong>mini-courses</strong> and <strong>deep-dive courses</strong>
            {" "}(see a simple sample to the right).
          </p>
          <p className="mt-3 text-[14px] leading-relaxed" style={{ color: INK, opacity: 0.65 }}>
            On the right is an interactive walkthrough of what a tailored course looks like inside
            the platform — click through it.
          </p>
        </div>
        <div className="col-span-12 md:col-span-7">
          <div className="rt-card overflow-hidden" data-testid="layerpath-tour">
            <div style={{ position: "relative", paddingBottom: "56.25%", height: 0, overflow: "hidden" }}>
              <iframe
                src={LAYERPATH_TOUR_URL}
                title="Interactive course tour"
                style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", border: 0 }}
                allowFullScreen
              />
            </div>
            <div className="p-4 flex items-center justify-between flex-wrap gap-2" style={{ borderTop: "1px solid #E5EDF5" }}>
              <div className="text-[13px]" style={{ color: INK }}>
                <strong>Interactive demo</strong> · a tailored course walkthrough
              </div>
              <a href={LAYERPATH_TOUR_URL} target="_blank" rel="noopener noreferrer"
                 className="text-[12px] font-bold inline-flex items-center gap-1" style={{ color: CYAN_DARK }}>
                Open fullscreen <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="mt-10">
        <div className="rt-eyebrow mb-3">Course types in the catalog</div>
        <div className="flex flex-wrap gap-2" data-testid="course-type-chips">
          {types.map((t) => (
            <span key={t} className="rt-chip">{t}</span>
          ))}
        </div>
      </div>
    </section>
  );
}

/* --------------------- BUILD YOUR OWN COURSE ------------------------------ */
function BuildYourOwn() {
  return (
    <section className="py-14 md:py-20">
      <div className="grid grid-cols-12 gap-8 items-center">
        <div className="col-span-12 md:col-span-7 order-2 md:order-1">
          <article className="rt-card overflow-hidden" data-testid="byo-video">
            <div className="aspect-video bg-black">
              <video src={REQUEST_TRAINING_VIDEO} controls preload="metadata" className="w-full h-full" />
            </div>
            <div className="p-4 text-[13px]" style={{ borderTop: "1px solid #E5EDF5", color: INK }}>
              <strong>Walkthrough</strong> · how your team requests a new course inside Guardian
            </div>
          </article>
        </div>
        <div className="col-span-12 md:col-span-5 order-1 md:order-2">
          <div className="rt-eyebrow mb-3">Build your own</div>
          <h2 className="rt-h text-3xl md:text-5xl">
            Need a course we don&rsquo;t have? <span className="rt-grad-text">Request it.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed" style={{ color: INK, opacity: 0.78 }}>
            Anyone on your team can submit a request from inside the platform — a new SEC risk alert,
            a Code of Ethics change, an incident response. The request lands with our team and goes
            straight into the authoring queue. We confirm scope, draft, and ship — typically inside
            a week, often sooner.
          </p>
          <a href={TRAINING_REQUEST_URL} target="_blank" rel="noopener noreferrer"
             className="mt-5 inline-flex items-center gap-2 text-[13px] font-bold"
             style={{ color: PINK_DARK }}>
            See the live request form on complisun.com <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>
    </section>
  );
}

/* --------------------- REGULATORY CALENDAR (living tracker) --------------- */
function StateLibrary() {
  const STATES = [
    { code: "ADV", name: "Form ADV annual amendment", note: "Within 90 days of fiscal year-end · Parts 1, 2A, 2B", pop: 14 },
    { code: "CRS", name: "Form CRS delivery",         note: "Amendments within 30 days · client delivery",         pop: 12 },
    { code: "COE", name: "Code of Ethics reports",    note: "Quarterly transactions · annual holdings",            pop: 11 },
    { code: "AR",  name: "Annual compliance review",  note: "Rule 206(4)-7 · written report",                       pop: 8 },
    { code: "MKT", name: "Marketing Rule review",     note: "Performance · testimonials · substantiation",          pop: 7 },
    { code: "CUS", name: "Custody & surprise exam",   note: "Rule 206(4)-2 · Form ADV-E",                            pop: 6 },
    { code: "ERI", name: "ERISA / 401(k) plans",      note: "408(b)(2) · committee minutes · 5500 support",         pop: 6 },
    { code: "AML", name: "AML program (2028)",        note: "FinCEN RIA rule · readiness track",                    pop: 4 },
  ];
  return (
    <section id="calendar" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-10">
        <div className="rt-eyebrow mb-3">A living regulatory calendar</div>
        <h2 className="rt-h text-4xl md:text-6xl max-w-4xl mx-auto">
          Every filing, report, and review — <span className="rt-grad-text">tracked around you.</span>
        </h2>
        <p className="mt-4 text-lg max-w-3xl mx-auto" style={{ color: INK, opacity: 0.75 }}>
          We sit with you to map exactly how tracking should work — filings, Code of Ethics reporting,
          the annual review, plan-level ERISA obligations — by deadline, by owner, by evidence required.
          From there each obligation is its own maintained sub-module. Rule changes roll in without
          rebuilding the program.
        </p>
      </div>

      <div className="rt-card p-6 md:p-7" data-testid="state-tracker">
        <div className="grid grid-cols-12 gap-3 items-center mb-4 text-[11px] font-extrabold uppercase tracking-[0.14em]"
             style={{ color: INK, opacity: 0.6 }}>
          <div className="col-span-3 sm:col-span-2">Obligation</div>
          <div className="col-span-3 sm:col-span-3">Tracked rule</div>
          <div className="col-span-3 sm:col-span-4">Coverage (sample)</div>
          <div className="col-span-3 sm:col-span-3 text-right">Status</div>
        </div>
        <div className="space-y-2.5">
          {STATES.map((s, idx) => {
            const widthPct = Math.min(100, (s.pop / 15) * 100);
            const isAttention = s.code === "MKT" || s.code === "AML";
            return (
              <div key={s.code} className="grid grid-cols-12 gap-3 items-center" data-testid={`state-${s.code.toLowerCase()}`}>
                <div className="col-span-3 sm:col-span-2 flex items-center gap-2">
                  <span className="h-9 w-9 rounded-lg grid place-items-center rt-grad-bg-cyan text-white flex-shrink-0">
                    <span className="rt-h text-[11px]">{s.code}</span>
                  </span>
                  <span className="text-[12px] font-bold hidden sm:inline leading-tight">{s.name}</span>
                </div>
                <div className="col-span-3 sm:col-span-3 text-[12px]" style={{ color: INK, opacity: 0.75 }}>
                  {s.note}
                </div>
                <div className="col-span-3 sm:col-span-4">
                  <div className="h-3 rounded-full overflow-hidden" style={{ background: `${CYAN_DARK}10` }}>
                    <div className="h-full rounded-full"
                         style={{
                           width: `${widthPct}%`,
                           background: isAttention ? `linear-gradient(90deg, ${PINK_LIGHT}, ${PINK_DARK})` : `linear-gradient(90deg, ${CYAN_LIGHT}, ${CYAN_DARK})`,
                         }} />
                  </div>
                </div>
                <div className="col-span-3 sm:col-span-3 text-right">
                  <span className="text-[10px] font-extrabold uppercase tracking-[0.14em] rounded-full px-2.5 py-1"
                        style={{
                          background: isAttention ? `${PINK_DARK}14` : `${CYAN_DARK}10`,
                          color: isAttention ? PINK_DARK : CYAN_DARK,
                        }}>
                    {isAttention ? "Pending update" : "Tracked"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
        <p className="mt-5 text-[12px]" style={{ color: INK, opacity: 0.55 }}>
          Coverage bars and statuses on this page are illustrative. Your real dashboard will reflect
          your services, custodians, plans, and the obligations you choose to track at launch.
        </p>
      </div>
    </section>
  );
}

/* --------------------- TRACKING SAMPLE (alert + bar chart) ---------------- */
function TrackingSample() {
  const rows = [
    { name: "Avery Okonkwo",  role: "Wealth Advisor",   completed: 95, mins: 132, comment: "Helpful — the pre-clearance walkthrough was the clearest I\u2019ve seen." },
    { name: "Marco Ruiz",     role: "Marketing",        completed: 88, mins: 118, comment: "Quiz felt fair. The testimonial and performance-ad case studies stuck." },
    { name: "Dana Whitfield", role: "Client Service",   completed: 80, mins: 96,  comment: "Loved the senior-investor scenario videos." },
    { name: "Priya Subramanian", role: "Compliance",    completed: 100, mins: 165, comment: "I want the Code of Ethics refresher quarterly, not annually." },
    { name: "Jasper Lee",     role: "Trading",          completed: 62, mins: 74,  comment: "Need to finish — quarter-end rebalancing crunch last week." },
    { name: "Renee Calloway", role: "401(k) Plans",     completed: 90, mins: 108, comment: "Captions on the ERISA fiduciary video were great." },
  ];
  const avgCompletion = Math.round(rows.reduce((s, r) => s + r.completed, 0) / rows.length);
  const totalMins = rows.reduce((s, r) => s + r.mins, 0);
  const incomplete = rows.filter((r) => r.completed < 80);

  return (
    <section id="tracking" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-10">
        <div className="rt-eyebrow mb-3 flex items-center justify-center gap-1.5">
          <BarChart3 className="h-3 w-3" /> Quarterly tracking sample
        </div>
        <h2 className="rt-h text-4xl md:text-6xl max-w-4xl mx-auto">
          See it through — <span className="rt-grad-text">with feedback loops.</span>
        </h2>
        <p className="mt-4 text-lg max-w-3xl mx-auto" style={{ color: INK, opacity: 0.75 }}>
          Each quarter we send a tracking pack: who finished, who needs a nudge, time spent per
          module, and a roll-up of what your employees told us. We pair the report with
          recommendations — refreshers to drop, modules to add, places where the team is asking for
          deeper coverage.
        </p>
      </div>

      {/* Alert card */}
      {incomplete.length > 0 && (
        <article
          className="rounded-2xl p-5 mb-5 flex items-start gap-4"
          style={{ background: `${PINK_LIGHT}14`, border: `1.5px solid ${PINK_DARK}55` }}
          data-testid="tracking-alert"
        >
          <div className="h-11 w-11 rounded-xl flex items-center justify-center flex-shrink-0"
               style={{ background: `${PINK_DARK}1A`, color: PINK_DARK }}>
            <AlertTriangle className="h-5 w-5" strokeWidth={2.2} />
          </div>
          <div className="flex-1">
            <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold" style={{ color: PINK_DARK }}>
              Auto-alert · sent to compliance leadership &amp; the assigned manager
            </div>
            <div className="rt-h text-lg mt-1">
              {incomplete[0].name} hasn&rsquo;t finished &ldquo;Code of Ethics &amp; Marketing Rule · Q2&rdquo; — completion sitting at {incomplete[0].completed}%.
            </div>
            <div className="text-[13px] mt-1.5" style={{ color: INK, opacity: 0.78 }}>
              Suggested next step: 1-click reminder + escalation reminder in 72 hours if still incomplete.
            </div>
          </div>
          <button type="button" disabled
                  className="hidden sm:inline-flex items-center gap-1.5 h-9 px-3 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em]"
                  style={{ background: PINK_DARK, color: "#fff" }}
                  data-testid="tracking-alert-action">
            <Bell className="h-3 w-3" /> Send reminder
          </button>
        </article>
      )}

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5" data-testid="tracking-stats">
        <Stat label="Team" value={rows.length} suffix="learners" />
        <Stat label="Avg completion" value={`${avgCompletion}%`} />
        <Stat label="Total time spent" value={`${Math.round(totalMins / 60)}h`} suffix={`${totalMins} min`} />
        <Stat label="Needs follow-up" value={incomplete.length} suffix="below 80%" pink />
      </div>

      {/* Bar chart + recommendations tabs */}
      <TrackingTabs rows={rows} />

      <p className="mt-5 text-center text-[12px]" style={{ color: INK, opacity: 0.55 }}>
        Names and metrics on this page are illustrative. Your real reports use your roster.
      </p>
    </section>
  );
}

function TrackingTabs({ rows }) {
  const [tab, setTab] = useState("completion");
  return (
    <article className="rt-card p-6 md:p-7" data-testid="tracking-bars">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <h3 className="rt-h text-xl">
          {tab === "completion" ? "Code of Ethics & Marketing Rule · Q2 completion by learner" : "Quarterly recommendations from CompliSun"}
        </h3>
        <div className="inline-flex rounded-full p-1" style={{ background: "#F4F8FC", border: `1px solid ${CYAN_LIGHT}33` }}>
          <button type="button" onClick={() => setTab("completion")}
                  data-testid="tracking-tab-completion"
                  className="px-3.5 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all"
                  style={{ background: tab === "completion" ? CYAN_DARK : "transparent", color: tab === "completion" ? "#fff" : CYAN_DARK }}>
            Completion
          </button>
          <button type="button" onClick={() => setTab("recommendations")}
                  data-testid="tracking-tab-recommendations"
                  className="px-3.5 py-1.5 rounded-full text-[11px] font-extrabold uppercase tracking-[0.14em] transition-all"
                  style={{ background: tab === "recommendations" ? PINK_DARK : "transparent", color: tab === "recommendations" ? "#fff" : PINK_DARK }}>
            Recommendations
          </button>
        </div>
      </div>

      {tab === "completion" && (
        <>
          <div className="space-y-3" data-testid="tracking-bars-list">
            {rows.map((r) => {
              const pct = r.completed;
              const alert = pct < 80;
              return (
                <div key={r.name} className="grid grid-cols-12 gap-3 items-center" data-testid={`tracking-row-${r.name.split(" ")[0].toLowerCase()}`}>
                  <div className="col-span-12 sm:col-span-3">
                    <div className="text-[13px] font-bold" style={{ color: INK }}>{r.name}</div>
                    <div className="text-[11px]" style={{ color: INK, opacity: 0.6 }}>{r.role}</div>
                  </div>
                  <div className="col-span-9 sm:col-span-6">
                    <div className="h-6 rounded-full overflow-hidden relative" style={{ background: `${CYAN_DARK}10` }}>
                      <div className="h-full rounded-full transition-all"
                           style={{
                             width: `${pct}%`,
                             background: alert ? `linear-gradient(90deg, ${PINK_LIGHT}, ${PINK_DARK})` : `linear-gradient(90deg, ${CYAN_LIGHT}, ${CYAN_DARK})`,
                           }} />
                      <span className="absolute inset-y-0 right-3 flex items-center text-[11px] font-extrabold"
                            style={{ color: pct > 35 ? "#fff" : INK }}>
                        {pct}%
                      </span>
                    </div>
                  </div>
                  <div className="col-span-3 sm:col-span-3 text-right text-[12px] font-bold tabular-nums" style={{ color: INK, opacity: 0.7 }}>
                    {r.mins} min
                  </div>
                </div>
              );
            })}
          </div>

          <div className="mt-6 pt-5" style={{ borderTop: `1px solid ${INK}10` }}>
            <div className="rt-eyebrow mb-3 flex items-center gap-1.5">
              <MessageSquare className="h-3 w-3" /> What your employees said
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {rows.slice(0, 4).map((r) => (
                <div key={r.name} className="rounded-xl p-3.5"
                     style={{ background: "#F4F8FC", border: `1px solid ${CYAN_LIGHT}33` }}>
                  <div className="text-[12px] font-extrabold" style={{ color: INK }}>{r.name} · {r.role}</div>
                  <div className="text-[13px] mt-1 italic" style={{ color: INK, opacity: 0.78 }}>
                    “{r.comment}”
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {tab === "recommendations" && (
        <div data-testid="tracking-recommendations">
          <p className="text-[14px] mb-4" style={{ color: INK, opacity: 0.78 }}>
            Drafted by counsel based on Q2 completion data, employee feedback, and the regulatory
            calendar. Each item links back to the underlying signal.
          </p>
          <ul className="space-y-3">
            {[
              {
                priority: "high",
                title: "Move the Code of Ethics refresher to quarterly",
                body: "Priya specifically asked for it; pairing the refresher with the quarterly transaction report deadline lifts on-time reporting. Quarterly drumbeat mirrors what examiners now expect to see.",
                source: "Employee feedback · Priya Subramanian",
              },
              {
                priority: "high",
                title: "Auto-escalate Jasper Lee's Marketing Rule refresher",
                body: "Sitting at 62% with one nudge already logged. We recommend a 72-hour escalation to his manager and a swap of the live segment for the 10-minute async version.",
                source: "Completion data · Jasper Lee (Trading)",
              },
              {
                priority: "medium",
                title: "Add a Form ADV / CRS sub-module before the annual amendment",
                body: "The SEC's latest exam priorities call out disclosure consistency across ADV Part 2 and the website. We've already drafted the module — pushes live with one click.",
                source: "SEC Division of Examinations · priorities",
              },
              {
                priority: "medium",
                title: "Re-record the ERISA fiduciary refresher with captions on by default",
                body: "Renee called out the captions specifically; analytics show 22% of viewers watched without audio.",
                source: "Employee feedback · Renee Calloway",
              },
              {
                priority: "low",
                title: "Trial the SMART Goals module with the Advisor track",
                body: "Behavioural-skills modules raise overall completion rates by ~8% on average. Low-cost experiment.",
                source: "Cross-client benchmark",
              },
            ].map((rec) => (
              <li key={rec.title} className="rounded-2xl p-4"
                  style={{ background: rec.priority === "high" ? `${PINK_LIGHT}10` : "#F4F8FC",
                           border: `1px solid ${rec.priority === "high" ? PINK_DARK : CYAN_LIGHT}55` }}
                  data-testid={`recommendation-${rec.title.toLowerCase().split(" ").slice(0, 3).join("-")}`}>
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] rounded-full px-2 py-0.5"
                            style={{
                              background: rec.priority === "high" ? PINK_DARK : (rec.priority === "medium" ? CYAN_DARK : `${INK}30`),
                              color: "#fff",
                            }}>
                        {rec.priority} priority
                      </span>
                    </div>
                    <h4 className="rt-h text-base">{rec.title}</h4>
                    <p className="text-[13px] mt-1 leading-relaxed" style={{ color: INK, opacity: 0.78 }}>{rec.body}</p>
                    <div className="text-[11px] mt-2 italic" style={{ color: INK, opacity: 0.55 }}>
                      Source: {rec.source}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
          <p className="mt-5 text-[12px]" style={{ color: INK, opacity: 0.55 }}>
            Recommendations land in your inbox the day after each quarter closes — and again any time
            a regulatory event materially changes the right answer.
          </p>
        </div>
      )}
    </article>
  );
}

function Stat({ label, value, suffix, pink }) {
  return (
    <div className="rounded-2xl px-4 py-3"
         style={{ background: pink ? `${PINK_LIGHT}14` : "#F4F8FC", border: `1px solid ${pink ? PINK_DARK : CYAN_LIGHT}33` }}>
      <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold"
           style={{ color: pink ? PINK_DARK : CYAN_DARK }}>{label}</div>
      <div className="rt-h text-2xl mt-0.5">{value}</div>
      {suffix && <div className="text-[11px]" style={{ color: INK, opacity: 0.6 }}>{suffix}</div>}
    </div>
  );
}

/* --------------------- ADD-ON MODULES (star to favourite) ----------------- */
function AddOnBundle({ picked, setPicked, viewerEmail }) {
  // Debounced star-ping: collect every new star in a 3-second window, then send
  // one SendGrid alert with all the IDs.
  const pendingPings = useRef(new Set());
  const pingTimer = useRef(null);
  // viewerEmail is captured through a ref so the unmount flush always uses the
  // latest value (e.g. if the user starred first, then typed their email into
  // the order form). Without this the cleanup closes over the initial null.
  const viewerEmailRef = useRef(viewerEmail);
  useEffect(() => { viewerEmailRef.current = viewerEmail; }, [viewerEmail]);

  const flushPings = useCallback(() => {
    const ids = Array.from(pendingPings.current);
    pendingPings.current = new Set();
    if (ids.length === 0) return;
    pingRtdStar(ids, viewerEmailRef.current || null);
  }, []);
  const queuePing = (id) => {
    pendingPings.current.add(id);
    if (pingTimer.current) clearTimeout(pingTimer.current);
    pingTimer.current = setTimeout(flushPings, 3000);
  };

  // Flush on unmount
  useEffect(() => {
    return () => {
      if (pingTimer.current) {
        clearTimeout(pingTimer.current);
        flushPings();
      }
    };
  }, [flushPings]);

  const ADDONS = [
    { id: "vendor",     icon: Building2,  title: "Vendor Management",       blurb: "Custodian, sub-adviser, and technology vendor due diligence with ongoing attestations." },
    { id: "monitoring", icon: Radio,      title: "Monitoring & Testing",    blurb: "Forensic tests of personal trading, marketing, fee billing, and best execution — quarterly." },
    { id: "risk",       icon: ShieldAlert,title: "Mock SEC Exam",           blurb: "A full document request and interview run by counsel — findings and remediation tracked." },
    { id: "policies",   icon: BookOpen,   title: "Own Your Policies & Procedures", blurb: "Versioned compliance manual mapped to Advisers Act obligations — you keep the keys." },
  ];
  const toggle = (id) => {
    setPicked((prev) => {
      const n = new Set(prev);
      if (n.has(id)) {
        n.delete(id);
      } else {
        n.add(id);
        queuePing(id); // only ping on ADD (not on un-star)
      }
      return n;
    });
  };
  return (
    <section id="addons" className="anchor-target py-14 md:py-20">
      <div
        className="relative rounded-3xl p-8 md:p-12 overflow-hidden"
        style={{ background: `linear-gradient(135deg, ${CYAN_DARK} 0%, ${INK} 100%)`, color: "#fff" }}
        data-testid="addon-bundle-card"
      >
        <div className="absolute -top-20 -right-20 w-80 h-80 rounded-full opacity-25 pointer-events-none"
             style={{ background: `radial-gradient(circle, ${PINK_LIGHT} 0%, transparent 70%)` }} />

        <div className="relative flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <div className="rt-eyebrow flex items-center gap-1.5" style={{ color: PINK_LIGHT }}>
              <Gift className="h-3 w-3" /> Grow with us · member rate after 90 days
            </div>
            <h2 className="rt-h text-3xl md:text-5xl mt-2 max-w-3xl">
              When you&rsquo;re ready, <span style={{ color: PINK_LIGHT }}>we go deeper.</span>
            </h2>
            <p className="mt-4 text-lg max-w-3xl" style={{ color: "rgba(255,255,255,0.84)" }}>
              Once RTD Financial is live, the rest of the CompliSun platform stacks on at the
              same speed and the same standard — and we train your team on every module we deploy.
              Tap the star on anything you&rsquo;d like more on.
              <strong> 20% off list</strong> after 90 days as an active client.
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[11px] font-extrabold uppercase tracking-[0.18em]"
                style={{ background: PINK_LIGHT, color: INK }}>
            20% loyalty discount
          </span>
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="addon-grid">
          {ADDONS.map((a) => {
            const on = picked.has(a.id);
            const Ic = a.icon;
            return (
              <button key={a.id} type="button" onClick={() => toggle(a.id)}
                      data-testid={`addon-${a.id}`} aria-pressed={on}
                      className="text-left rounded-2xl p-5 transition-all relative"
                      style={{
                        background: on ? "#fff" : "rgba(255,255,255,0.08)",
                        border: `1.5px solid ${on ? PINK_LIGHT : "rgba(255,255,255,0.18)"}`,
                        color: on ? INK : "#fff",
                      }}>
                <div className="absolute top-3.5 right-3.5">
                  <Star
                    className="h-5 w-5 transition-all"
                    fill={on ? PINK_DARK : "transparent"}
                    style={{ color: on ? PINK_DARK : "rgba(255,255,255,0.5)" }}
                    strokeWidth={1.8}
                    data-testid={`addon-star-${a.id}`}
                  />
                </div>
                <div className="h-10 w-10 rounded-xl flex items-center justify-center mb-3"
                     style={{ background: on ? `${PINK_LIGHT}33` : "rgba(255,255,255,0.12)", color: on ? PINK_DARK : PINK_LIGHT }}>
                  <Ic className="h-5 w-5" strokeWidth={2} />
                </div>
                <h3 className="rt-h text-lg leading-tight" style={{ color: on ? INK : "#fff" }}>{a.title}</h3>
                <p className="text-[13px] mt-1.5 leading-relaxed"
                   style={{ color: on ? `${INK}b3` : "rgba(255,255,255,0.78)" }}>
                  {a.blurb}
                </p>
                <div className="mt-3 text-[10px] font-extrabold uppercase tracking-[0.14em]"
                     style={{ color: on ? PINK_DARK : PINK_LIGHT }}>
                  {on ? "Starred" : "Tap the star to mark interest"}
                </div>
              </button>
            );
          })}
        </div>

        {picked.size > 0 && (
          <div className="relative mt-6 rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3"
               style={{ background: "rgba(255,255,255,0.10)", border: `1.5px solid ${PINK_LIGHT}66` }}
               data-testid="addon-summary">
            <div className="text-[14px]" style={{ color: "#fff" }}>
              <strong>{picked.size} starred</strong> — included on your order form below.
            </div>
            <a href="#order" className="rt-cta-pink inline-flex items-center gap-2 h-10 px-5 text-sm whitespace-nowrap">
              See the order form <ArrowRight className="h-4 w-4" />
            </a>
          </div>
        )}
      </div>
    </section>
  );
}

/* --------------------- PRICING -------------------------------------------- */
function Pricing({ seats, setSeats }) {
  const PER_SEAT = 30;
  const annual = seats * PER_SEAT;
  const onSeats = (v) => {
    const n = Math.max(1, Math.min(2000, parseInt(v, 10) || 0));
    setSeats(n);
  };
  return (
    <section id="pricing" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-12">
        <div className="rt-eyebrow mb-3">Added bonus · Training</div>
        <h2 className="rt-h text-4xl md:text-6xl">
          Training on top — <span className="rt-grad-text">$30</span> per seat, per year.
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: INK, opacity: 0.7 }}>
          The plan above is the engagement — this is the bonus that comes with it.
          The whole course catalog for every advisor and staff member, no setup fee, annual billing.
        </p>
      </div>

      <div className="rt-card p-7 md:p-9 max-w-3xl mx-auto" data-testid="pricing-card">
        <div className="flex items-end justify-center gap-3 flex-wrap">
          <span className="rt-h text-7xl rt-grad-text">$30</span>
          <div className="text-left">
            <div className="text-[15px] font-bold" style={{ color: INK }}>per learner</div>
            <div className="text-[15px]" style={{ color: INK, opacity: 0.7 }}>per year</div>
          </div>
        </div>

        <div className="mt-7 rounded-2xl p-5" style={{ background: "#F4F8FC" }}>
          <div className="flex items-end gap-4 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="text-[11px] uppercase tracking-[0.18em] font-bold" style={{ color: CYAN_DARK }}>
                Your team size
              </label>
              <div className="flex items-center gap-3 mt-2">
                <input type="range" min="1" max="500" value={Math.min(500, seats)}
                       onChange={(e) => onSeats(e.target.value)}
                       data-testid="pricing-slider"
                       className="flex-1" style={{ accentColor: CYAN_DARK }} />
                <input type="number" min="1" max="2000" value={seats}
                       onChange={(e) => onSeats(e.target.value)}
                       data-testid="pricing-seats"
                       className="w-24 text-center rounded-full py-2 px-3 font-extrabold"
                       style={{ border: `2px solid ${CYAN_LIGHT}`, color: INK }} />
              </div>
              <div className="text-[12px] mt-1.5" style={{ color: INK, opacity: 0.55 }}>
                Pre-set to ~40 — drag to match your headcount.
              </div>
            </div>
            <div className="rounded-2xl p-5 text-right" style={{ background: "#fff", border: `2px solid ${CYAN_LIGHT}88` }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: CYAN_DARK }}>
                Your annual
              </div>
              <div className="rt-h text-4xl rt-grad-text" data-testid="pricing-annual">
                ${annual.toLocaleString()}
              </div>
              <div className="text-[12px]" style={{ color: INK, opacity: 0.65 }}>
                ~${Math.round(annual / 12).toLocaleString()}/mo equivalent
              </div>
            </div>
          </div>
        </div>

        <ul className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-3">
          {[
            "Full counsel-authored catalog access",
            "Maintained multi-state library",
            "Per-module knowledge checks + attestation",
            "Examiner-ready reporting & export",
            "Quarterly tracking pack with recommendations",
            "Hosts your or third-party content alongside ours",
            "No setup fee · no platform configuration charge",
          ].map((b) => (
            <li key={b} className="flex items-start gap-2 text-[14px]" style={{ color: INK, opacity: 0.88 }}>
              <CheckCircle2 className="h-4 w-4 mt-0.5 flex-shrink-0" style={{ color: CYAN_DARK }} />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}

/* --------------------- TIMELINE ------------------------------------------- */
function fmtShort(d) {
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function Timeline() {
  const [kickoff, setKickoff] = useState(() => new Date().toISOString().slice(0, 10));
  const base = new Date(`${kickoff}T12:00:00`);
  const addDays = (n) => { const d = new Date(base); d.setDate(d.getDate() + n); return d; };
  const proto = addDays(14);
  const full = addDays(45);
  const fullLong = full.toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

  return (
    <section id="timeline" className="anchor-target py-14 md:py-20">
      <div className="text-center mb-8">
        <div className="rt-eyebrow mb-3">Timeline · yours to set</div>
        <h2 className="rt-h text-4xl md:text-6xl">
          Pick your kickoff. <span className="rt-grad-text">We handle the rest.</span>
        </h2>
        <p className="mt-4 text-lg max-w-2xl mx-auto" style={{ color: INK, opacity: 0.75 }}>
          Demo next week — prototype included. Fully functional within 45 days of sign-off, well before the
          12/31–1/31 renewal. Start using the platform and catalog <strong>immediately</strong> alongside your
          current vendor; the monthly fee begins only once you've transitioned off it.
        </p>
        <div className="mt-6 inline-flex flex-wrap items-center justify-center gap-3 rt-card px-5 py-4">
          <label className="text-[13px] font-bold" style={{ color: INK }} htmlFor="timeline-kickoff">
            When do you want to kick off?
          </label>
          <input
            id="timeline-kickoff"
            type="date"
            value={kickoff}
            min={new Date().toISOString().slice(0, 10)}
            onChange={(e) => e.target.value && setKickoff(e.target.value)}
            data-testid="timeline-kickoff"
            className="rt-input !w-auto"
          />
          <span className="text-[13px] font-bold" style={{ color: PINK_DARK }} data-testid="timeline-full-date">
            → fully functional by {fullLong}
          </span>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="timeline-grid">
        {[
          { label: `Day 0 · ${fmtShort(base)}`, title: "Sign-off + install — use it immediately", body: "Install paid (or first installment). Platform access and the existing catalog are usable from day one." },
          { label: `~2 weeks · ${fmtShort(proto)}`, title: "Prototype refined", body: "The prototype from your demo, tuned to your modules, workflows, and migrated sample records — with experts running a gap analysis alongside." },
          { label: "Weeks 3–5", title: "Migrate & pilot", body: "Records imported from your current vendor; pilot group runs the program in parallel; feedback rolls into polish." },
          { label: `Day 45 · ${fmtShort(full)}`, title: "Fully live · monthly fee begins", body: "Whole firm live and off the old platform — Code of Ethics reporting, filings calendar, training, your dedicated expert on cadence. Only now does $1,000/mo start." },
        ].map((s, i) => (
          <article key={s.label} className="rt-card p-5" data-testid={`timeline-${i}`}>
            <div className="rt-eyebrow" style={{ color: i === 3 ? PINK_DARK : CYAN_DARK }}>{s.label}</div>
            <h3 className="rt-h text-lg mt-1.5">{s.title}</h3>
            <p className="text-[13px] mt-2 leading-relaxed" style={{ color: INK, opacity: 0.78 }}>{s.body}</p>
          </article>
        ))}
      </div>
    </section>
  );
}

/* --------------------- ORDER FORM (replaces Book the Call) ---------------- */
function OrderForm({ starredAddons, seats, engagementPct, sectionsViewed, onEmailChange }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [tracksText, setTracksText] = useState("");
  const [readyDate, setReadyDate] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Propagate the email upward so the star-ping can attribute it.
  useEffect(() => {
    if (email && email.includes("@")) onEmailChange?.(email);
  }, [email, onEmailChange]);

  const ADDON_LABEL = {
    vendor:     "Vendor Management",
    monitoring: "Monitoring & Testing",
    risk:       "Mock SEC Exam",
    policies:   "Own Your Policies & Procedures",
  };

  // Split free-text into trimmed items (commas, newlines, or bullets).
  const parsedTracks = tracksText
    .split(/[,\n;•·]+/)
    .map((t) => t.trim())
    .filter(Boolean);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) {
      toast.error("Name and email are required");
      return;
    }
    setBusy(true);
    try {
      await submitRtdOrder({
        name: name.trim(),
        email: email.trim(),
        seats,
        tracks: parsedTracks,
        addons: Array.from(starredAddons || []),
        target_go_live: readyDate || null,
        notes: notes.trim() || null,
        engagement_pct: engagementPct,
        sections_viewed: sectionsViewed || [],
      });
      setDone(true);
      toast.success("Order received — Colin will follow up to confirm.");
    } catch (err) {
      toast.error("Couldn't send — email colindarke@complisun.com directly.");
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <section id="order" className="anchor-target py-14 md:py-20">
        <div className="rt-card max-w-2xl mx-auto p-10 text-center" data-testid="order-form-done">
          <CheckCircle2 className="h-12 w-12 mx-auto mb-4" style={{ color: CYAN_DARK }} />
          <h2 className="rt-h text-3xl">Order received.</h2>
          <p className="mt-3 text-[15px] max-w-md mx-auto" style={{ color: INK, opacity: 0.75 }}>
            Colin will confirm scope and lock the timeline — prototype in ~2 weeks, fully functional within 45 days.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section id="order" className="anchor-target py-14 md:py-20">
      <div className="grid grid-cols-12 gap-8 items-start">
        <div className="col-span-12 md:col-span-5">
          <div className="rt-eyebrow mb-3">Training order form</div>
          <h2 className="rt-h text-3xl md:text-5xl">
            Want training first? <span className="rt-grad-text">Send it to Colin.</span>
          </h2>
          <p className="mt-5 text-[15px] leading-relaxed" style={{ color: INK, opacity: 0.78 }}>
            The Plan Builder above covers the platform and team. If you'd like to start with training
            alone, mark the tracks you want at launch, confirm starred add-ons from above, and add anything
            else. Submitting routes a structured order straight to Colin so prep can begin immediately.
          </p>
          <div className="mt-6 rounded-2xl p-4" style={{ background: "#F4F8FC", border: `1px solid ${CYAN_LIGHT}33` }}>
            <div className="rt-eyebrow mb-1.5">Your order so far</div>
            <ul className="text-[13px] space-y-1" style={{ color: INK }}>
              <li>• <strong>{seats}</strong> seats · ${(seats * 30).toLocaleString()}/year</li>
              <li>• <strong>{parsedTracks.length}</strong> track{parsedTracks.length === 1 ? "" : "s"} at launch</li>
              <li>• <strong>{(starredAddons || new Set()).size}</strong> starred add-on{(starredAddons || new Set()).size === 1 ? "" : "s"}</li>
              <li>• Engagement: <strong>{engagementPct}%</strong> of the proposal</li>
            </ul>
          </div>
        </div>

        <form onSubmit={submit} data-testid="order-form"
              className="col-span-12 md:col-span-7 rt-card p-6 md:p-8 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Name *">
              <input type="text" value={name} onChange={(e) => setName(e.target.value)}
                     data-testid="order-name" placeholder="Full name" className="rt-input" />
            </Field>
            <Field label="Work email *">
              <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                     data-testid="order-email" placeholder="you@rtdfinancial.com" className="rt-input" />
            </Field>
          </div>

          <Field label="Tracks to launch with" hint="Free-form: roles, teams, or scenarios — separate with commas or new lines. E.g., Advisors · Client Service · Trading · 401(k) team · All Staff.">
            <textarea
              value={tracksText}
              onChange={(e) => setTracksText(e.target.value)}
              data-testid="order-tracks"
              rows={3}
              placeholder="Advisors, Client Service, Operations, Trading, All Staff…"
              className="rt-input resize-none"
            />
            {parsedTracks.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1.5" data-testid="order-tracks-chips">
                {parsedTracks.map((t, i) => (
                  <span key={`${t}-${i}`} className="rt-chip text-[11px]">{t}</span>
                ))}
              </div>
            )}
          </Field>

          <Field label="Target go-live (optional)" hint="Leaving blank uses the 45-day timeline above.">
            <input type="date" value={readyDate} onChange={(e) => setReadyDate(e.target.value)}
                   data-testid="order-date" className="rt-input" />
          </Field>

          <Field label="Notes for Colin">
            <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                      data-testid="order-notes"
                      placeholder="Specific courses you want first, your compliance manual or Code of Ethics to send over, anything else."
                      className="rt-input resize-none" />
          </Field>

          {(starredAddons || new Set()).size > 0 && (
            <div className="rounded-2xl p-4" style={{ background: `${PINK_LIGHT}14`, border: `1px solid ${PINK_DARK}55` }}>
              <div className="text-[10px] uppercase tracking-[0.18em] font-extrabold mb-2" style={{ color: PINK_DARK }}>
                Starred add-ons travelling with this order
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Array.from(starredAddons).map((id) => (
                  <span key={id} className="rt-chip-pink text-[11px]">
                    <Star className="inline h-3 w-3 mr-1" fill={PINK_DARK} style={{ color: PINK_DARK }} />
                    {ADDON_LABEL[id] || id}
                  </span>
                ))}
              </div>
            </div>
          )}

          <button type="submit" disabled={busy} data-testid="order-submit"
                  className="rt-cta-pink w-full h-12 inline-flex items-center justify-center gap-2 text-base disabled:opacity-50">
            {busy ? <><Loader2 className="h-4 w-4 animate-spin" /> Sending order…</>
                  : <>Send order to Colin <Send className="h-4 w-4" /></>}
          </button>
        </form>
      </div>

      <style>{`
        .rt-input { width: 100%; background: #fff; border: 1.5px solid #E5EDF5; border-radius: 12px; padding: 11px 14px; font-size: 14px; color: ${INK}; font-family: inherit; transition: border-color .15s ease, box-shadow .15s ease; }
        .rt-input:focus { outline: none; border-color: ${CYAN_LIGHT}; box-shadow: 0 0 0 4px ${CYAN_LIGHT}33; }
      `}</style>
    </section>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-xs font-extrabold uppercase tracking-[0.16em] mb-1.5"
            style={{ color: INK, opacity: 0.75 }}>
        {label}
      </span>
      {children}
      {hint && (
        <span className="block text-[11px] mt-1" style={{ color: INK, opacity: 0.55 }}>{hint}</span>
      )}
    </label>
  );
}

/* --------------------- CLOSE ---------------------------------------------- */
function Close() {
  return (
    <section id="close" className="anchor-target py-14 md:py-24">
      <div className="rounded-3xl p-10 md:p-16 text-center relative overflow-hidden"
           style={{ background: `linear-gradient(135deg, ${CYAN_LIGHT} 0%, ${CYAN_DARK} 60%, ${PINK_DARK} 100%)`, color: "#fff" }}
           data-testid="close-card">
        <h2 className="rt-h text-4xl md:text-6xl max-w-3xl mx-auto">
          Built around RTD Financial. Live before your renewal.
        </h2>
        <p className="mt-5 text-lg max-w-2xl mx-auto" style={{ color: "rgba(255,255,255,0.92)" }}>
          Pick a plan, share the one-page summary with your team for sign-off, and we'll have your prototype ready for a live demo early next week.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <a href="#build" data-testid="close-order-cta"
             className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full text-base font-extrabold transition-transform hover:-translate-y-0.5"
             style={{ background: "#fff", color: INK }}>
            Open the Plan Builder <ArrowRight className="h-4 w-4" />
          </a>
          <a href="mailto:colindarke@complisun.com?subject=RTD Financial%20%E2%80%94%20follow-up"
             className="inline-flex items-center justify-center gap-2 h-14 px-8 rounded-full text-base font-bold"
             style={{ background: "rgba(255,255,255,0.14)", color: "#fff", border: "1.5px solid rgba(255,255,255,0.3)" }}>
            <Mail className="h-4 w-4" /> Email Colin
          </a>
        </div>
      </div>
    </section>
  );
}

/* --------------------- FOOTER --------------------------------------------- */
function Footer() {
  return (
    <footer className="mt-10 py-10 px-6 md:px-10" data-testid="rtd-footer"
            style={{ background: INK, color: "#fff" }}>
      <div className="max-w-6xl mx-auto flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <div className="flex items-center gap-3">
          <img src="/brand/complisun-mark.png" alt="CompliSun" className="h-9 w-9 object-contain" />
          <div>
            <div className="rt-h text-base">CompliSun</div>
            <div className="text-xs" style={{ opacity: 0.7 }}>Compliance Simplified</div>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm" style={{ opacity: 0.85 }}>
          <div className="flex items-start gap-2">
            <MapPin className="h-4 w-4 mt-0.5" style={{ color: CYAN_LIGHT }} />
            <span>1420 Washington Blvd., Suite 301<br />Detroit, MI 48226</span>
          </div>
          <a href="mailto:colindarke@complisun.com" className="flex items-start gap-2">
            <Mail className="h-4 w-4 mt-0.5" style={{ color: CYAN_LIGHT }} />
            <span>colindarke@complisun.com</span>
          </a>
          <a href="#order" className="flex items-start gap-2">
            <Calendar className="h-4 w-4 mt-0.5" style={{ color: CYAN_LIGHT }} />
            <span>Order form</span>
          </a>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-8 pt-6 text-xs leading-relaxed"
           style={{ borderTop: `1px solid rgba(255,255,255,0.15)`, opacity: 0.6 }}>
        © {new Date().getFullYear()} CompliSun · Detroit, MI. Tailored proposal for RTD Financial — a compliance program for an SEC-registered investment adviser.
        Marketing communication subject to Michigan Rules of Professional Conduct 7.1.
      </div>
    </footer>
  );
}
